import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Modal } from 'bootstrap'
import { Formik, Form, FormikValues } from "formik"
import * as Yup from 'yup'

import { KTSVG } from '../../../_metronic/helpers'
import { StatusErrorMessage, FormikNotesArea } from '../widgets'
 
import { SubmitButtonRow } from '../layout'

import { cloneDeep } from 'lodash'
import { IWebFileAttachment } from '../../modules/core/applications'
import { processNoteValues } from '../helpers'

interface IApplicationEditResponseApprovalModel  {
  verdict: string
  note: string,
  noteAttachments: Array<IWebFileAttachment>    
}

const initApplicationEditResponseApprovalValues:  IApplicationEditResponseApprovalModel = {
  verdict: '',
  note: '',
  noteAttachments: []
}

const ApplicationEditResponseApprovalSchema: any = Yup.object({
  verdict: Yup.string().trim().required(),
  note: Yup.string().trim().label('Note')
  .when('verdict', {
    is: (val: string) => (val !== 'approved'),
    then: (schema) => schema.required(),
  }),        
  noteAttachments: Yup.array(),    
});


type ApplicationEditResponseDialogProps = {
  title: string
  message: string
  onOK: Function
  onCancel?: Function    
}

export type ApplicationEditResponseDialogRef = {
  show: Function
  hide: Function
}

const ApplicationEditResponseDialog = forwardRef<ApplicationEditResponseDialogRef, ApplicationEditResponseDialogProps>(({title, message, onOK, onCancel}, ref) => {
    
  useImperativeHandle(ref, () => ({        
    show: () => showModal(),       
    hide: () => hideModal()
  }));

  const modalRef = useRef<HTMLDivElement | null>(null);

  const [ initValues, setInitValues ] = useState<IApplicationEditResponseApprovalModel>(cloneDeep(initApplicationEditResponseApprovalValues));
  const [ verdict, setVerdict] = useState('');

  const showModal = () => {        
    if (! (modalRef && modalRef.current)) return;
    // reset values
    setInitValues(cloneDeep(initApplicationEditResponseApprovalValues));
    setVerdict('');

    const modalEle = modalRef.current;
    const bsModal = new Modal(modalEle, {
      backdrop: false,
      keyboard: false
    })
    bsModal.show()                
  }

  const hideModal = () => {        
    if (! (modalRef && modalRef.current)) return;
    const modalEle = modalRef.current;
    const bsModal= Modal.getInstance(modalEle);
    if (bsModal) {
      bsModal.hide()            
    }
    if (onCancel) onCancel();         
  }

  const doSubmit = (values: any, actions: FormikValues) => {        
    const { setStatus, setSubmitting } = actions;
    setStatus('');
    
    if (onOK) {
      let payload = { verdict: values.verdict};
      processNoteValues(values, payload);
      onOK(payload);
    }

    hideModal();
    setSubmitting(false);
  }

  return (
  <div className="modal" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="staticBackdropLabel" aria-hidden="true">
    <div className="modal-dialog min-w-650px ">
      <div className="modal-content">
        <div className="modal-header">
          <h1 className="modal-title fs-5" id="staticBackdropLabel">{title}</h1>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={hideModal}></button>
        </div>
        <div className="modal-body">
          <div className="text-center">
            {message}
          </div>                    
          <Formik
            initialValues={initValues}
            validationSchema={ApplicationEditResponseApprovalSchema}
            enableReinitialize={true}
            onSubmit={doSubmit}
          >
          {({status,setFieldValue, setStatus}) => (
          <Form id='kt_application_edit_response_form' className='form' noValidate>
            <div
              className='d-flex flex-column scroll-y me-n7 ps-2 pe-7'
              id='kt_doclabel_submitresponse_scroll'
              data-kt-scroll='true'
              data-kt-scroll-activate='{default: false, lg: true}'
              data-kt-scroll-max-height='auto'
              data-kt-scroll-dependencies='#kt_submit_review_header'
              data-kt-scroll-wrappers='#kt_submit_review_scroll'
              data-kt-scroll-offset='300px'
            >
              <div className="text-muted w-normal fs-6 mb-2">Select verdict:</div>
              <div className='fv-row mb-2 d-flex flex-row gap-4'>
                <div
                  className='flex-root' onClick={() => {
                    setVerdict('approved');
                    setFieldValue('verdict', 'approved');
                    setFieldValue('note', '');
                    setFieldValue('noteAttachments', []);                                      
                    setStatus('');
                  }}
                >
                  <input
                    id='kt_submit_review_type_approved'
                    type='radio'
                    className='btn-check'
                    name="verdict"
                    value='approved'
                  />
                  <label
                    className={'btn py-2 px-6 d-flex flex-row align-items-center mb-2 ' + (verdict === 'approved' ? 'btn-success text-white' : ' btn-outline btn-outline-dashed btn-outline-default')}
                    htmlFor='kt_submit_review_type_approved'
                  >
                    <KTSVG
                      path='/media/icons/duotune/abstract/abs009.svg'
                      className='svg-icon-2x me-5'
                    />

                    <div className='flex-grow-1 fw-bold text-center pe-10'>
                      <span className='fw-bolder d-block fs-4'>APPROVE</span>
                    </div>
                  </label>
                </div>
                <div className='flex-root'
                  onClick={() => {
                    setVerdict('rejected');
                    setFieldValue('verdict', 'rejected');
                    setStatus('');
                  }}
                >
                  <input
                    id='kt_submit_review_type_rejected'
                    type='radio'
                    className='btn-check'
                    name="verdict"
                    value='rejected'
                  />
                  <label
                    className={'btn py-2 px-6 d-flex flex-row align-items-center mb-2 ' + (verdict === 'rejected' ? 'btn-danger' : ' btn-outline btn-outline-dashed btn-outline-default')}
                    htmlFor='kt_submit_review_type_rejected'
                  >
                    <KTSVG
                      path='/media/icons/duotune/abstract/abs012.svg'
                      className='svg-icon-2x me-5'
                    />
                    <div className='flex-grow-1 fw-bold text-center pe-10'>
                      <span className='fw-bolder d-block fs-4'>REJECT</span>
                    </div>
                  </label>
                </div>                              
              </div>

              { (verdict !== '') &&
              <>                           
              { (verdict !== 'approved') &&
              <FormikNotesArea required />
              }
              <StatusErrorMessage status={status} />
              <SubmitButtonRow doClose={() => hideModal()} />
              </>
              }
            </div>
          </Form>
          )}
          </Formik>
        </div>                
      </div>
    </div>
  </div>
  )
});

export { ApplicationEditResponseDialog }